import { dangerLimit, forbiddenGoals, GOALS, type CompanionContract, type GoalKind } from '../companions/contracts.ts';
import { assessDanger, type DangerReport } from './danger.ts';
import type { CombatState } from '../combat/index.ts';

/**
 * Deterministic utility-scored goals. Every goal scores from the contract's
 * discussed preferences, the companion's temperament and the evaluated danger;
 * obligations and refuse-goal boundaries veto with a recorded reason instead of
 * scoring. Ties break on goal name, never on iteration order or randomness.
 */
export type ScoredGoal = Readonly<{ goal: GoalKind; score: number }>;
export type VetoedGoal = Readonly<{ goal: GoalKind; reason: 'obligation-forbids' | 'boundary-non-negotiable' | 'danger-exceeds-temperament' }>;

export type GoalAssessment = Readonly<{
  scored: readonly ScoredGoal[];
  vetoed: readonly VetoedGoal[];
  chosen: GoalKind | null;
  danger: DangerReport;
}>;

function score(contract: Readonly<CompanionContract>, goal: GoalKind, danger: number): number {
  const base = contract.preferences[goal] * 100;
  const { boldness, loyalty } = contract.temperament;
  if (goal === 'engage') return base + boldness * 20 - danger * 5;
  if (goal === 'guard') return base + loyalty * 20 - danger * 2;
  if (goal === 'hold') return base + (10 - boldness) * 10 - danger * 2;
  return base + danger * 14 - boldness * 20 - 150;
}

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

export function scoreGoals(contract: Readonly<CompanionContract>, danger: DangerReport, targetId?: string): GoalAssessment {
  const forbidden = new Set(forbiddenGoals(contract, targetId));
  const limit = dangerLimit(contract);
  const overLimit = limit !== null && danger.level > limit;
  const scored: ScoredGoal[] = [];
  const vetoed: VetoedGoal[] = [];
  for (const goal of GOALS) {
    if (forbidden.has(goal)) {
      const obligation = contract.obligations.some(obligation => obligation.forbiddenGoals.includes(goal));
      vetoed.push({ goal, reason: obligation ? 'obligation-forbids' : 'boundary-non-negotiable' });
      continue;
    }
    if (overLimit && goal !== 'withdraw') {
      vetoed.push({ goal, reason: 'danger-exceeds-temperament' });
      continue;
    }
    scored.push({ goal, score: score(contract, goal, danger.level) });
  }
  scored.sort((a, b) => b.score - a.score || compareText(a.goal, b.goal));
  return { scored, vetoed, chosen: scored[0]?.goal ?? null, danger };
}

function stillDecides(state: Readonly<CombatState>, companionId: string): boolean {
  const actor = state.actors[companionId];
  return Boolean(actor && actor.role === 'companion' && actor.status !== 'dead' && actor.hp > 0);
}

const SILENT_DEAD: GoalAssessment = {
  scored: [],
  vetoed: [],
  chosen: null,
  danger: { level: 0, enemies: 0, nearestDistance: null },
};

/**
 * Score goals for one living companion from a live tactical snapshot.
 * Death is permanent: a dead companion chooses nothing and records no new
 * refusal. Missing HP must not be read as live danger after they are gone.
 */
export function evaluateCompanion(
  contract: Readonly<CompanionContract>,
  state: Readonly<CombatState>,
): GoalAssessment {
  if (!stillDecides(state, contract.companionId)) return SILENT_DEAD;
  return scoreGoals(contract, assessDanger(state, contract.companionId));
}
